import { useEffect, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '../lib/api';

const STORAGE_KEY = 'ann_views';

const getViewsToday = (annId) => {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const today = new Date().toISOString().split('T')[0];
    if (data.date !== today) return 0;
    return data[annId] || 0;
  } catch { return 0; }
};

const incrementViews = (annId) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    let data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (data.date !== today) data = { date: today };
    data[annId] = (data[annId] || 0) + 1;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const AnnouncementModal = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [phase, setPhase] = useState('hidden'); // hidden | entering | visible | leaving
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) return;
    const fetchAnnouncements = async () => {
      try {
        const res = await api.get('/announcements/active');
        const list = res.data || [];
        const eligible = list.filter(a => getViewsToday(a.id) < a.max_views_per_day);
        if (eligible.length > 0) {
          const pick = eligible[Math.floor(Math.random() * eligible.length)];
          setAnnouncement(pick);
          incrementViews(pick.id);
          setTimeout(() => setPhase('entering'), 100);
          setTimeout(() => setPhase('visible'), 150);
        }
      } catch {}
    };
    const timer = setTimeout(fetchAnnouncements, 1200);
    return () => clearTimeout(timer);
  }, [isAdminPage]);

  const dismiss = useCallback(() => {
    setPhase('leaving');
    setTimeout(() => { setPhase('hidden'); setAnnouncement(null); }, 600);
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) dismiss();
  };

  if (!announcement || phase === 'hidden') return null;

  const isLeaving = phase === 'leaving';
  const isVisible = phase === 'visible' || phase === 'entering';

  return (
    <>
      <style>{`
        @keyframes annGenieIn {
          0% { transform: scale(0.15) perspective(800px) rotateX(35deg) rotateY(-10deg) translateY(60vh); opacity: 0; filter: blur(8px); }
          40% { transform: scale(0.6) perspective(800px) rotateX(10deg) rotateY(-3deg) translateY(10vh); opacity: 0.7; filter: blur(2px); }
          70% { transform: scale(1.02) perspective(800px) rotateX(-2deg) rotateY(1deg) translateY(-1vh); opacity: 1; filter: blur(0); }
          85% { transform: scale(0.99) perspective(800px) rotateX(1deg) rotateY(0deg) translateY(0); }
          100% { transform: scale(1) perspective(800px) rotateX(0) rotateY(0) translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes annGenieOut {
          0% { transform: scale(1) perspective(800px) rotateX(0) rotateY(0) translateY(0); opacity: 1; filter: blur(0); }
          20% { transform: scale(1.02) perspective(800px) rotateX(-3deg) rotateY(2deg) translateY(-1vh); }
          50% { transform: scale(0.5) perspective(800px) rotateX(15deg) rotateY(-5deg) translateY(20vh); opacity: 0.5; filter: blur(3px); }
          100% { transform: scale(0.05) perspective(800px) rotateX(40deg) rotateY(-15deg) translateY(70vh); opacity: 0; filter: blur(12px); }
        }
        @keyframes annBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes annBackdropOut { from { opacity: 1; } to { opacity: 0; } }
        .ann-modal-enter { animation: annGenieIn 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .ann-modal-leave { animation: annGenieOut 0.55s cubic-bezier(0.55, 0, 1, 0.45) forwards; }
        .ann-backdrop-enter { animation: annBackdropIn 0.4s ease forwards; }
        .ann-backdrop-leave { animation: annBackdropOut 0.5s ease forwards; }
      `}</style>
      <div
        className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isLeaving ? 'ann-backdrop-leave' : 'ann-backdrop-enter'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        onClick={handleBackdropClick}
        data-testid="announcement-overlay"
      >
        <div
          className={`relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${isLeaving ? 'ann-modal-leave' : 'ann-modal-enter'}`}
          style={{ backgroundColor: '#fff', transformOrigin: 'center bottom' }}
          data-testid="announcement-modal"
        >
          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center transition-colors"
            data-testid="announcement-close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* Header accent */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #C19A6B, #8B6F47, #C19A6B)' }} />

          {/* Title */}
          <div className="px-6 pt-5 pb-3">
            <h2 className="text-xl font-bold text-[#3d2a1f] text-center" dir="rtl">{announcement.title}</h2>
          </div>

          {/* Content */}
          <div className="px-6 pb-6" dir="rtl">
            {announcement.content_type === 'image' && announcement.image_url ? (
              <img src={announcement.image_url} alt={announcement.title} className="w-full rounded-xl max-h-[60vh] object-contain" />
            ) : (
              <div className="prose prose-sm max-w-none text-[#3d2a1f] leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(announcement.content) }} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnouncementModal;
